// SCR-RET-01..05 (frozen mockups-ret-v1): the Returns page (Return invoices · Adjustments), the customer-return picker,
// the return sheet for a paid invoice (RET-01..03), the adjustment sheet for an unpaid invoice (RET-04, ADR-117), the
// return card with its paper document (ADR-109/110) and the receipt reversal sheet (SAL-08), all saved in two steps.
import { useCallback, useRef, useState, type ReactNode, type RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  BottomSheet,
  Button,
  Chips,
  ContextStrip,
  EmptyState,
  Field,
  Input,
  List,
  ListRow,
  Notice,
  PaperDoc,
  PaperDocTable,
  PaperTotals,
  QuickActions,
  Select,
  SheetActions,
  SkeletonList,
  SummaryCard,
  Tabs,
} from '../../design/kit';
import {
  RETURNS,
  adjustUnpaidInvoice,
  loadAdjustments,
  loadReturn,
  loadReturnable,
  loadReturns,
  loadRoundReturns,
  postCustomerReturn,
  reverseCollection,
  type LineChoice,
  type Returnable,
} from '../../data/returns';
import { SALES, loadSales, type Collection, type Sale } from '../../data/sales';
import { ROUNDS, loadLiveRound } from '../../data/rounds';
import { CASH } from '../../data/cash';
import { loadSettings } from '../../data/settings';
import { useSession } from '../../app/session';
import { useCardHeader } from '../../app/Shell';
import { Code, Name } from '../../i18n/Isolate';
import { businessLanguage, directionOf, type Language } from '../../i18n';
import { businessToday, formatDate, formatMoney } from '../../lib/format';
import { shareDocument } from '../../lib/ux/share';
import { messageKey } from '../../lib/errors';
import { refusalCode, useCustomers, useTab } from '../catalog/common';
import { useTrucks } from '../rounds/TrucksPage';
import { countWord } from '../stock/StockSheets';

function useMoney() {
  const { i18n } = useTranslation();
  const lang = i18n.language as Language;
  return { lang, money: (v: number) => formatMoney(v, lang) };
}

/** The live round of the signed-in Seller's truck (returns are made inside it). */
function useMyRound() {
  const { userId, role } = useSession();
  const trucks = useTrucks();
  const truck = (trucks.data ?? []).find((k) => role === 'seller' && k.seller_id === userId);
  const round = useQuery({
    queryKey: [...ROUNDS, 'live', truck?.id],
    queryFn: () => loadLiveRound(truck?.id ?? ''),
    enabled: Boolean(truck),
  });
  return { truck, round: round.data ?? null, loading: trucks.isLoading || round.isLoading };
}

const TABS = ['returns', 'adjustments'] as const;

/** SCR-RET-01: the Seller sees their round's returns and adjustments; the owner sees every round's. */
export function ReturnsPage() {
  const { t } = useTranslation();
  const { role } = useSession();
  const { money, lang } = useMoney();
  const navigate = useNavigate();
  const seller = role === 'seller';
  const [tab, setTab] = useTab(TABS, 'returns');
  const [picking, setPicking] = useState(false);
  const [truckFilter, setTruckFilter] = useState('');
  const trucks = useTrucks();
  const { truck, round, loading } = useMyRound();
  const roundId = seller ? round?.id : undefined;
  const returns = useQuery({
    queryKey: [...RETURNS, 'list', roundId ?? 'all'],
    queryFn: () => loadReturns({ round: roundId }),
    enabled: !seller || Boolean(roundId),
  });
  const adjustments = useQuery({
    queryKey: [...RETURNS, 'adjustments', roundId ?? 'all'],
    queryFn: () => loadAdjustments({ round: roundId }),
    enabled: !seller || Boolean(roundId),
  });
  const totals = useQuery({
    queryKey: [...RETURNS, 'round', roundId],
    queryFn: () => loadRoundReturns(roundId ?? ''),
    enabled: Boolean(roundId),
  });
  if (seller && loading) return <SkeletonList label={t('loading')} />;
  const open = seller && round?.status === 'open';
  // The owner may narrow both lists to one truck (the context strip's truck).
  const shownReturns = (returns.data ?? []).filter((r) => !truckFilter || r.truck_id === truckFilter);
  const shownAdjustments = (adjustments.data ?? []).filter((a) => !truckFilter || a.truck_id === truckFilter);
  const refunded = seller ? (totals.data?.refunded ?? 0) : shownReturns.reduce((s, r) => s + r.refund, 0);
  const adjusted = seller
    ? (totals.data?.adjusted ?? 0)
    : shownAdjustments.reduce((s, a) => s + a.total_before - a.total_after, 0);
  const stripTruck = seller ? truck : (trucks.data ?? []).find((k) => k.id === truckFilter);
  return (
    <>
      {/* GEN-14: a truck page shows its context — the Seller's own truck, or the truck the owner filters on. */}
      <ContextStrip
        truck={
          <Field label={t('truck')}>
            {({ id }) =>
              seller ? (
                <Input id={id} dir="ltr" readOnly value={truck?.code ?? '—'} />
              ) : (
                <Select id={id} value={truckFilter} onChange={(e) => setTruckFilter(e.target.value)}>
                  <option value="">{t('p7.allTrucks')}</option>
                  {(trucks.data ?? []).map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.code}
                    </option>
                  ))}
                </Select>
              )
            }
          </Field>
        }
        round={
          <Field label={t('tk.seller')}>
            {({ id }) => <Input id={id} dir="auto" readOnly value={stripTruck?.seller_name ?? '—'} />}
          </Field>
        }
        workingDate={
          <Field label={t('workdate')}>
            {({ id }) => <Input id={id} dir="ltr" readOnly value={formatDate(businessToday(), lang)} />}
          </Field>
        }
      />
      <SummaryCard
        label={t(seller ? 'rt.sum' : 'rt.sumall')}
        value={money(refunded + adjusted)}
        figures={[
          { label: t('rt.refunded'), value: money(refunded) },
          {
            label: t('rt.adjusts'),
            value: String(seller ? (totals.data?.adjustments ?? 0) : shownAdjustments.length),
          },
        ]}
      />
      {seller && (
        <QuickActions
          actions={[
            {
              id: 'return',
              label: t('act.custreturn'),
              icon: 'return' as const,
              main: true,
              onSelect: () => setPicking(true),
            },
          ].filter(() => open)}
        />
      )}
      {seller && !open && <Notice tone="info">{t('rt.noround')}</Notice>}
      <Tabs
        label={t('p.returns')}
        value={tab}
        onChange={(v) => setTab(v as (typeof TABS)[number])}
        items={[
          { id: 'returns', label: t('tab.rtninv') },
          { id: 'adjustments', label: t('tab.adjust') },
        ]}
      />
      {tab === 'returns' ? (
        !returns.data ? (
          <SkeletonList label={t('loading')} />
        ) : shownReturns.length === 0 ? (
          <EmptyState icon="return" title={t('rt.empty')} text={t('rt.empty.text')} />
        ) : (
          <List label={t('tab.rtninv')}>
            {shownReturns.map((r) => (
              <ListRow
                key={r.id}
                testId={`rtn-${r.number}`}
                lead={{ initials: r.customer.slice(0, 2) }}
                title={<Name>{r.customer}</Name>}
                onOpen={() => navigate(`/returns/${r.id}`)}
                sub={
                  <>
                    <Code>{r.number}</Code> · {t('rt.of', { n: r.sale_number })} ·{' '}
                    <bdi className="yv-nowrap">{formatDate(r.business_date, lang)}</bdi>
                    <br />
                    <Name>{r.reason}</Name>
                  </>
                }
                value={money(r.refund)}
                badge={
                  <>
                    <Badge tone="info">{t('rt.cashrefund')}</Badge>
                    {r.has_damaged && <Badge tone="warning">{t('rt.damaged')}</Badge>}
                  </>
                }
              />
            ))}
          </List>
        )
      ) : !adjustments.data ? (
        <SkeletonList label={t('loading')} />
      ) : shownAdjustments.length === 0 ? (
        <EmptyState icon="return" title={t('rt.empty')} text={t('rt.empty.text')} />
      ) : (
        <List label={t('tab.adjust')}>
          {shownAdjustments.map((a) => (
            <ListRow
              key={a.id}
              testId={`adj-${a.sale_number}`}
              lead={{ initials: a.customer.slice(0, 2) }}
              title={<Name>{a.customer}</Name>}
              sub={
                <>
                  <Code>{a.sale_number}</Code> · <bdi className="yv-nowrap">{formatDate(a.business_date, lang)}</bdi> ·{' '}
                  {t('aj.was', { a: money(a.total_before) })}
                  <br />
                  <Name>{a.reason}</Name>
                </>
              }
              value={money(a.total_after)}
              badge={<Badge>{t('rt.adjbadge')}</Badge>}
            />
          ))}
        </List>
      )}
      {picking && round && truck && (
        <CustomerReturnSheet
          roundId={round.id}
          truckCode={truck.code}
          roundCash={Number(round.cash ?? 0)}
          onClose={() => setPicking(false)}
        />
      )}
    </>
  );
}

/** "Customer return": choose the customer, then one of their invoices; a paid one is returned, an unpaid one adjusted. */
function CustomerReturnSheet({
  roundId,
  truckCode,
  roundCash,
  onClose,
}: {
  roundId: string;
  truckCode: string;
  roundCash: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { userId } = useSession();
  const { money, lang } = useMoney();
  const customers = useCustomers();
  const [customer, setCustomer] = useState('');
  const [sale, setSale] = useState<Sale | null>(null);
  const invoices = useQuery({
    queryKey: [...SALES, 'customer', customer],
    queryFn: () => loadSales({ customer }),
    enabled: Boolean(customer),
  });
  // RET-01: own paid invoices; RET-04 / ADR-117: unpaid invoices this Seller holds.
  const eligible = (invoices.data ?? []).filter((s) =>
    s.status === 'paid' ? s.seller_id === userId : s.collector_id === userId,
  );
  if (sale)
    return (
      <LineSheet
        sale={sale}
        roundId={roundId}
        truckCode={truckCode}
        roundCash={roundCash}
        onClose={onClose}
        onBack={() => setSale(null)}
      />
    );
  return (
    <BottomSheet title={t('act.custreturn')} open onClose={onClose}>
      <Field label={t('rt.pickcust')}>
        {({ id }) => (
          <Select id={id} value={customer} onChange={(e) => setCustomer(e.target.value)}>
            <option value="">—</option>
            {(customers.data ?? [])
              .filter((c) => c.active)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </Select>
        )}
      </Field>
      {customer && !invoices.data && <SkeletonList label={t('loading')} />}
      {customer && invoices.data && eligible.length === 0 && <Notice tone="info">{t('rt.noinvoice')}</Notice>}
      {eligible.length > 0 && (
        <List label={t('rt.pickinv')}>
          {eligible.map((s) => (
            <ListRow
              key={s.id}
              testId={`pick-${s.number}`}
              lead={{ icon: 'file' }}
              title={<Code>{s.number}</Code>}
              onOpen={() => setSale(s)}
              sub={<bdi className="yv-nowrap">{formatDate(s.business_date, lang)}</bdi>}
              value={money(Number(s.total))}
              badge={
                <Badge tone={s.status === 'paid' ? 'success' : 'warning'}>
                  {t(s.status === 'paid' ? 'rt.paidinv' : 'rt.unpaidinv')}
                </Badge>
              }
            />
          ))}
        </List>
      )}
      <SheetActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
      </SheetActions>
    </BottomSheet>
  );
}

/** SCR-RET-02 (paid) and SCR-RET-03 (unpaid): quantities per line, a reason, then a summary and Confirm. */
function LineSheet({
  sale,
  roundId,
  truckCode,
  roundCash,
  onClose,
  onBack,
}: {
  sale: Sale;
  roundId: string;
  truckCode: string;
  roundCash: number;
  onClose: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const { money, lang } = useMoney();
  const qc = useQueryClient();
  const paid = sale.status === 'paid';
  const lines = useQuery({ queryKey: [...RETURNS, 'returnable', sale.id], queryFn: () => loadReturnable(sale.id) });
  const [qty, setQty] = useState<Record<number, string>>({});
  const [damaged, setDamaged] = useState<number[]>([]);
  const [reason, setReason] = useState('');
  const [summary, setSummary] = useState(false);
  const [problem, setProblem] = useState('');
  const chosen: (LineChoice & { row: Returnable })[] = (lines.data ?? [])
    .map((row) => ({
      row,
      line: row.sale_line_id,
      pieces: Number(qty[row.sale_line_id] || 0),
      damaged: damaged.includes(row.sale_line_id),
    }))
    .filter((c) => c.pieces > 0);
  const value = chosen.reduce((s, c) => s + Math.round(c.row.unit_net * c.pieces * 100) / 100, 0);
  const until = lines.data?.[0]?.returnable_until;
  const save = useMutation({
    mutationFn: async (): Promise<unknown> => {
      const payload = chosen.map(({ line, pieces, damaged: d }) =>
        paid ? { line, pieces, damaged: Boolean(d) } : { line, pieces },
      );
      return paid
        ? postCustomerReturn(roundId, sale.id, payload, reason)
        : adjustUnpaidInvoice(roundId, sale.id, payload, reason);
    },
    onSuccess: () => {
      for (const key of [RETURNS, SALES, ROUNDS, CASH]) void qc.invalidateQueries({ queryKey: key });
      onClose();
    },
    onError: (e) => setProblem(t(messageKey(refusalCode(e)))),
  });
  const review = () => {
    setProblem('');
    if (chosen.length === 0) return setProblem(t('err.nothingret'));
    const over = chosen.find((c) => c.pieces > c.row.pieces_left || !Number.isInteger(c.pieces));
    if (over) return setProblem(t('err.overret', { q: countWord(t, 'piece', over.row.pieces_left) }));
    if (!reason.trim()) return setProblem(t('err.noreason'));
    if (paid && value > roundCash) return setProblem(t('err.lowcash', { a: money(roundCash) }));
    setSummary(true);
  };
  return (
    <BottomSheet
      title={`${t(paid ? 's82.title' : 'aj.title')} · ${sale.number}`}
      open
      onClose={onClose}
      busy={save.isPending}
    >
      <List label={sale.number}>
        <ListRow
          title={<Name>{sale.customer}</Name>}
          sub={until && t('rt.until', { d: formatDate(until, lang) })}
          value={<bdi className="yv-nowrap">{formatDate(sale.business_date, lang)}</bdi>}
        />
      </List>
      {!lines.data ? (
        <SkeletonList label={t('loading')} />
      ) : summary ? (
        <>
          {paid ? (
            <Notice tone="info">
              {t('rt.sumline', {
                q: chosen.map((c) => `${countWord(t, 'piece', c.pieces)} ${c.row.product}`).join(', '),
                k: truckCode,
                d: chosen.some((c) => c.damaged) ? t('rt.damaged') : t('rt.sellable'),
                a: money(value),
              })}
            </Notice>
          ) : (
            <Notice tone="info">
              {t('aj.nocash', { k: truckCode })} {t('aj.keep')}
            </Notice>
          )}
          <List label={t('rt.refund')}>
            {paid ? (
              <>
                <ListRow title={<b>{t('rt.refund')}</b>} value={<b>{money(value)}</b>} />
                <ListRow
                  title={t('rt.after')}
                  sub={t('aj.was', { a: money(roundCash) })}
                  value={money(roundCash - value)}
                />
              </>
            ) : (
              <ListRow
                title={<Code>{sale.number}</Code>}
                sub={t('aj.was', { a: money(Number(sale.total)) })}
                value={<b>{money(Number(sale.total) - value)}</b>}
              />
            )}
          </List>
          {problem && <Notice tone="danger">{problem}</Notice>}
          <SheetActions>
            <Button onClick={() => setSummary(false)}>{t('back')}</Button>
            <Button variant="main" busy={save.isPending} onClick={() => save.mutate()}>
              {t(paid ? 'rt.save' : 'aj.save')}
            </Button>
          </SheetActions>
        </>
      ) : (
        <>
          <List label={sale.number}>
            {lines.data.map((row) => (
              <ListRow
                key={row.sale_line_id}
                title={<Name>{row.product}</Name>}
                sub={`${t('rt.sold', { q: row.sold, r: row.sold - row.pieces_left })} · ${t('rt.unit', { p: money(row.unit_net) })}`}
                actions={
                  <>
                    <Field label={t(paid ? 'rt.qty' : 'aj.removed')}>
                      {({ id }) => (
                        <Input
                          id={id}
                          numeric
                          inputMode="numeric"
                          dir="ltr"
                          value={qty[row.sale_line_id] ?? ''}
                          onChange={(e) => setQty((q) => ({ ...q, [row.sale_line_id]: e.target.value }))}
                        />
                      )}
                    </Field>
                    {paid && (
                      <Chips
                        label={t('rt.damaged')}
                        items={[{ id: String(row.sale_line_id), label: t('rt.damaged') }]}
                        selected={damaged.includes(row.sale_line_id) ? [String(row.sale_line_id)] : []}
                        onToggle={() =>
                          setDamaged((d) =>
                            d.includes(row.sale_line_id)
                              ? d.filter((x) => x !== row.sale_line_id)
                              : [...d, row.sale_line_id],
                          )
                        }
                      />
                    )}
                  </>
                }
              />
            ))}
          </List>
          <Field label={t('rt.reason')}>
            {({ id }) => <Input id={id} dir="auto" value={reason} onChange={(e) => setReason(e.target.value)} />}
          </Field>
          <List label={t('rt.refund')}>
            <ListRow
              title={<b>{t(paid ? 'rt.refund' : 'aj.newtotal')}</b>}
              value={<b data-testid="return-total">{money(paid ? value : Number(sale.total) - value)}</b>}
            />
            {paid && <ListRow title={t('rt.roundcash', { a: '' }).trim()} value={money(roundCash)} />}
          </List>
          {problem && <Notice tone="danger">{problem}</Notice>}
          <SheetActions>
            <Button onClick={onBack}>{t('back')}</Button>
            <Button variant="main" onClick={review}>
              {t('c8.next')}
            </Button>
          </SheetActions>
        </>
      )}
    </BottomSheet>
  );
}

const CARD_TABS = ['return', 'document'] as const;

/** SCR-RET-04: the return invoice and its paper document (business language), shared as an image. */
export function ReturnCard() {
  const { t, i18n } = useTranslation();
  const { id = '' } = useParams();
  const { money, lang } = useMoney();
  const navigate = useNavigate();
  const [tab, setTab] = useTab(CARD_TABS, 'return');
  const paper = useRef<HTMLElement>(null);
  const ret = useQuery({ queryKey: [...RETURNS, 'one', id], queryFn: () => loadReturn(id) });
  const settings = useQuery({ queryKey: ['settings', 'v_settings'], queryFn: loadSettings });
  const back = useCallback(() => navigate(-1), [navigate]);
  useCardHeader(ret.data?.number ?? t('p.returns'), back);
  if (ret.isLoading) return <SkeletonList label={t('loading')} />;
  const r = ret.data;
  if (!r) return <Notice tone="warning">{t('err.notfound')}</Notice>;
  const dl = businessLanguage();
  const dt = i18n.getFixedT(dl);
  const dmoney = (v: number) => formatMoney(v, dl);
  return (
    <>
      <SummaryCard
        label={r.customer}
        value={money(r.refund)}
        sub={
          <>
            {t('rt.of', { n: r.sale_number })} · <bdi className="yv-nowrap">{formatDate(r.business_date, lang)}</bdi> ·{' '}
            <Code>{r.truck_code}</Code>
            {r.seller_name && (
              <>
                {' '}
                · <Name>{r.seller_name}</Name>
              </>
            )}
          </>
        }
      />
      <Tabs
        label={r.number}
        value={tab}
        onChange={(v) => setTab(v as (typeof CARD_TABS)[number])}
        items={[
          { id: 'return', label: t('tab.rtn') },
          { id: 'document', label: t('tab.rtndoc') },
        ]}
      />
      {tab === 'return' ? (
        <>
          <List label={r.number}>
            {r.lines.map((l) => (
              <ListRow
                key={l.id}
                title={<Name>{l.product}</Name>}
                sub={`${countWord(t, 'piece', l.pieces)} × ${money(l.unit_net)}`}
                value={money(l.value)}
                badge={l.damaged ? <Badge tone="warning">{t('rt.damaged')}</Badge> : undefined}
              />
            ))}
          </List>
          <Notice tone="info">
            {t('rt.reason')}: <Name>{r.reason}</Name>
          </Notice>
        </>
      ) : (
        <>
          <div lang={dl} dir={directionOf(dl)}>
            <PaperDocRef paper={paper}>
              <PaperDoc
                businessName={settings.data?.business_name ?? 'YakVender'}
                documentTitle={dt('doc.return')}
                number={<Code>{r.number}</Code>}
                date={formatDate(r.business_date, dl)}
                party={
                  <>
                    {dt('doc.customer')}: <Name>{r.customer}</Name> · {dt('doc.original')}: <Code>{r.sale_number}</Code>{' '}
                    <bdi className="yv-nowrap">{formatDate(r.sale_date, dl)}</bdi>
                  </>
                }
                stamp={dt('doc.refunded')}
              >
                <PaperDocTable>
                  <thead>
                    <tr>
                      <th>{dt('doc.product')}</th>
                      <th className="num">{dt('doc.qty')}</th>
                      <th className="num">{dt('doc.price')}</th>
                      <th className="num">{dt('doc.net')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {r.lines.map((l) => (
                      <tr key={l.id}>
                        <td>
                          <Name>{l.product}</Name>
                        </td>
                        <td className="num">{countWord(dt, 'piece', l.pieces)}</td>
                        <td className="num">{dmoney(l.unit_net)}</td>
                        <td className="num">{dmoney(l.value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </PaperDocTable>
                <PaperTotals>
                  <b>
                    {dt('doc.total')} {dmoney(r.refund)}
                  </b>
                </PaperTotals>
              </PaperDoc>
            </PaperDocRef>
          </div>
          <QuickActions
            actions={[
              {
                id: 'share',
                label: t('act.share'),
                icon: 'file',
                main: true,
                onSelect: () => {
                  if (paper.current)
                    void shareDocument(
                      paper.current,
                      r.number,
                      r.number,
                      `${dt('doc.return')} ${r.number} · ${dmoney(r.refund)}`,
                    );
                },
              },
            ]}
          />
        </>
      )}
    </>
  );
}

/** Wraps the PaperDoc so the share action can capture it as an image. */
function PaperDocRef({ paper, children }: { paper: RefObject<HTMLElement>; children: ReactNode }) {
  return (
    <section ref={paper} aria-label="document">
      {children}
    </section>
  );
}

/** SCR-RET-05: the owner reverses a collection with a reason; a summary first (SAL-08, ADR-116). */
export function ReverseCollectionSheet({ collection, onClose }: { collection: Collection; onClose: () => void }) {
  const { t } = useTranslation();
  const { money } = useMoney();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [summary, setSummary] = useState(false);
  const [problem, setProblem] = useState('');
  const save = useMutation({
    mutationFn: () => reverseCollection(collection.id, reason),
    onSuccess: () => {
      for (const key of [SALES, ROUNDS, CASH, RETURNS]) void qc.invalidateQueries({ queryKey: key });
      onClose();
    },
    onError: (e) => setProblem(t(messageKey(refusalCode(e)))),
  });
  return (
    <BottomSheet title={`${t('rv.title')} · ${collection.number}`} open onClose={onClose} busy={save.isPending}>
      <List label={collection.number}>
        <ListRow
          title={<Name>{collection.customer}</Name>}
          sub={t('rv.paid', { n: (collection.invoices ?? []).join(' · ') })}
          value={money(Number(collection.amount))}
        />
      </List>
      {summary ? (
        <>
          <Notice tone="warning">
            {t('rv.sumshort', { a: money(Number(collection.amount)), n: (collection.invoices ?? []).join(' · ') })}
          </Notice>
          {problem && <Notice tone="danger">{problem}</Notice>}
          <SheetActions>
            <Button onClick={() => setSummary(false)}>{t('back')}</Button>
            <Button variant="main" busy={save.isPending} onClick={() => save.mutate()}>
              {t('rv.save')}
            </Button>
          </SheetActions>
        </>
      ) : (
        <>
          <Field label={t('rt.reason')}>
            {({ id }) => <Input id={id} dir="auto" value={reason} onChange={(e) => setReason(e.target.value)} />}
          </Field>
          {problem && <Notice tone="danger">{problem}</Notice>}
          <SheetActions>
            <Button onClick={onClose}>{t('cancel')}</Button>
            <Button
              variant="main"
              onClick={() => {
                if (!reason.trim()) return setProblem(t('err.noreason'));
                setProblem('');
                setSummary(true);
              }}
            >
              {t('c8.next')}
            </Button>
          </SheetActions>
        </>
      )}
    </BottomSheet>
  );
}
